import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ConservationStatusChart = ({ iucnCategories }) => {
    const [activeCat, setActiveCat] = useState(iucnCategories[2]); // Default to 'CR'

    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-12 lg:mt-20 max-w-6xl mx-auto px-4 sm:px-6"
        >
            <div className="max-w-4xl mx-auto text-center mb-16 lg:mb-24">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <h2 className="text-3xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-6 sm:mb-8 font-display leading-[1.2] sm:leading-[1.1]">
                        Conservation <span className="text-emerald-600 italic">Framework.</span>
                    </h2>

                    <p className="text-sm sm:text-lg text-slate-500 font-medium leading-relaxed max-w-2xl mx-auto italic">
                        The IUCN Red List of Threatened Species™ is the world's most comprehensive inventory of the global conservation status of biological species.
                    </p>
                </motion.div>
            </div>

            <div className="grid lg:grid-cols-12  gap-5 lg:gap-8 items-stretch">
                {/* Visual Scale / Selector */}
                <div className="lg:col-span-4 xl:col-span-5 md:col-span-6 bg-white rounded-[20px] lg:rounded-[20px] p-5 lg:p-7 flex flex-col justify-between shadow-2xl relative overflow-hidden group border border-slate-100">
                    {/* Decorative Background Element */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-slate-50 blur-[100px] rounded-full -translate-y-1/2 translate-x-1/2" />

                    <div>
                        <span className="text-[10px] lg:text-[11px] font-black text-emerald-600 uppercase tracking-[0.3em] mb-4 lg:mb-6 block">Interactive Scale</span>

                        {/* Responsive Scrollable Track for Mobile, Grid list for Desktop */}
                        <div className="flex lg:grid lg:grid-cols-2 overflow-x-auto lg:overflow-x-visible pb-3 lg:pb-0 gap-3 lg:gap-x-8 lg:gap-y-4 lg:space-y-0 scrollbar -mx-2 px-2 lg:mx-0 lg:px-0">
                            {iucnCategories.map((cat) => (
                                <button
                                    key={cat.code}
                                    onMouseEnter={() => setActiveCat(cat)}
                                    onClick={() => setActiveCat(cat)}
                                    className={`
                                        flex-shrink-0 flex items-center gap-3 lg:gap-4 p-2.5 lg:p-3 rounded-xl transition-all duration-300 group/btn
                                        ${activeCat.code === cat.code ? 'bg-slate-100 ring-1 ring-slate-200 shadow-sm' : 'hover:bg-slate-50 opacity-60 hover:opacity-100'}
                                    `}
                                >
                                    <div
                                        className="w-7 h-7 lg:w-9 lg:h-9 rounded-full flex items-center justify-center font-black text-[9px] lg:text-[10px] shadow-lg transition-transform group-hover/btn:scale-110 flex-shrink-0"
                                        style={{ backgroundColor: cat.color, color: cat.textColor }}
                                    >
                                        {cat.code}
                                    </div>
                                    <div className="text-left overflow-hidden">
                                        <h4 className="text-[9px] lg:text-[10px] font-bold text-slate-900 uppercase tracking-widest leading-tight">{cat.name}</h4>
                                        <div className={`h-0.5 rounded-full mt-1 transition-all duration-500 ${activeCat.code === cat.code ? 'w-full bg-emerald-500' : 'w-0 bg-slate-200'}`} />
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mt-8 lg:mt-10 pt-6 lg:pt-6 border-t border-slate-100 hidden lg:block">
                        <p className="text-slate-400 text-[8px] lg:text-[9px] uppercase tracking-widest font-bold text-center">
                            Hover or tap to explore details
                        </p>
                    </div>
                </div>

                {/* Detail Panel */}
                <div className="lg:col-span-8 xl:col-span-7 md:col-span-6 bg-white border border-slate-200 rounded-[25px] lg:rounded-[32px] p-6 lg:p-10 shadow-premium relative flex flex-col overflow-hidden">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeCat.code}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.4 }}
                            className="flex flex-col h-full overflow-y-auto no-scrollbar"
                        >
                            <div className="flex items-center gap-4 lg:gap-5 mb-5 lg:mb-6">
                                <div
                                    className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl flex items-center justify-center text-lg font-black shadow-2xl"
                                    style={{ backgroundColor: activeCat.color, color: activeCat.textColor }}
                                >
                                    {activeCat.code}
                                </div>
                                <div>
                                    <h2 className="text-xl lg:text-2xl font-bold text-slate-900 uppercase tracking-tight mb-0.5">{activeCat.name}</h2>
                                    <span className="px-2 py-0.5 bg-slate-100 text-[8px] lg:text-[9px] font-black text-slate-400 uppercase tracking-widest rounded-full">Official IUCN Status</span>
                                </div>
                            </div>

                            <div className="space-y-4 lg:space-y-6">
                                <div>
                                    <h5 className="text-[8px] lg:text-[9px] font-black text-emerald-600 uppercase tracking-[0.2em] mb-2.5">Detailed Description</h5>
                                    <p className="text-slate-600 leading-relaxed text-xs lg:text-sm font-medium">
                                        {activeCat.longDesc}
                                    </p>
                                </div>

                                <div className="grid md:grid-cols-2 gap-4 lg:gap-5">
                                    <div className="bg-slate-50 rounded-xl lg:rounded-2xl p-4 lg:p-5 border border-slate-100">
                                        <h5 className="text-[7px] lg:text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Key Criteria</h5>
                                        <p className="text-slate-700 text-[10px] lg:text-[11px] leading-relaxed italic">
                                            "{activeCat.criteria}"
                                        </p>
                                    </div>
                                    <div className="bg-emerald-50 rounded-xl lg:rounded-2xl p-4 lg:p-5 border border-emerald-100">
                                        <h5 className="text-[7px] lg:text-[8px] font-black text-emerald-600 uppercase tracking-[0.2em] mb-2">Conservation Impact</h5>
                                        <p className="text-emerald-900 text-[10px] lg:text-[11px] leading-relaxed font-medium">
                                            {activeCat.implication}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-auto pt-6 lg:pt-6 border-t border-slate-100 flex items-center justify-between">
                                <a href="https://www.iucnredlist.org/resources/categories-and-criteria" target="_blank" rel="noopener noreferrer" className="text-[8px] lg:text-[9px] font-black text-slate-400 hover:text-emerald-600 transition-colors uppercase tracking-widest flex items-center gap-2">
                                    Official Source <span className="hidden sm:inline">Documentation</span>
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                </a>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </motion.div>
    );
};

export default ConservationStatusChart;
